import invariant from 'invariant';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { makeDonation } from 'api/donations';
import { Box } from 'components/Box';
import { CmsSocialButtons } from 'components/CmsSocialButtons';
import { Divider } from 'components/Divider';
import { Link } from 'components/Link';
import { Body } from 'components/Text/Body';
import { RichText } from 'components/Text/RichText';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsApiDonateBlockData } from 'modules/cms/api/types/blocks/CmsApiDonateBlock';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import {
  colors,
  cssBreakpoints,
  fontWeights,
  gutterWidth,
  maxWidth,
  textStyles,
} from 'theme/theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { getCookieValue } from 'utils/cookies';
import { imageUrl } from 'utils/filestack';
import { filterObject, mapObject } from 'utils/functional';
import { scrollToTop } from 'utils/scroll';
import { snakeCaseToCamelCase } from 'utils/string';
import { hiDPI } from 'utils/style';

import { CmsDonateBlockInternalForm } from './CmsDonateBlockInternalForm';

const Container = styled(Box)<{
  $imageHandle: string | undefined;
  $mimetype: string | undefined;
}>`
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  ${({ $imageHandle, $mimetype }) =>
    $imageHandle &&
    `
    background-image: url('${imageUrl({
      data: { handle: $imageHandle, mimetype: $mimetype },
      width: 800,
      height: undefined,
      fit: 'max',
      insecureSVG: false,
    })}');
    ${hiDPI()}: {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1600,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
    }
    @media all and (min-width: ${cssBreakpoints.lgUp}) {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1280,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
      ${hiDPI()}: {
        background-image: url('${imageUrl({
          data: { handle: $imageHandle, mimetype: $mimetype },
          width: 2560,
          height: undefined,
          fit: 'max',
          insecureSVG: false,
        })}');
      }
    }
  `}
`;
const Headline = styled(
  ({
    text,
    isPageHeader,
    ...props
  }: {
    text: string;
    isPageHeader: boolean;
  }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  ),
)`
  && {
    h1,
    h2 {
      /* block-no-empty */
      ${textStyles.h2};
    }
  }
`;
const StyledSocialButtons = styled(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: any) => (
    <Box
      className={props.className}
      display="flex"
      my={20}
      mx="-6px"
      flexDirection="row"
      data-qa-id="donate-thank-you-social-buttons"
    >
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <CmsSocialButtons {...props} />
    </Box>
  ),
)``;

type Props = {
  data: CmsApiDonateBlockData;
  isPageHeader?: boolean;
  content: CmsApiPage;
};

export function CmsDonateBlock({
  data: { headline, body, thankYouHeadline, thankYouBody, campaignId, bgImage },
  isPageHeader,
  content,
}: Props) {
  const { user, environment } = useUserEnvironment();
  const location = useLocation();
  useEffect(() => {
    trackEvent('View Donate Form', {
      amount: 0,
      campaign_id: campaignId,
    });
  }, [campaignId]);
  const [invoiceId, setInvoiceId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState(25);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = ({ handleError, updateSubmitting, values }: any) => {
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    const extraParamsFromCookie = JSON.parse(getCookieValue('extraParams'));
    const extraParamsFromSearch = filterObject(
      queryString.parse(location.search),
      // @ts-expect-error TS(2339): Property 'startsWith' does not exist on type 'stri... Remove this comment to see the full error message
      (k) => k.startsWith('utm') || k === 'sf_campaign_id',
    );
    const extraParams = mapObject(
      { ...extraParamsFromCookie, ...extraParamsFromSearch },
      // @ts-expect-error TS(2345): Argument of type 'string | number | symbol' is not... Remove this comment to see the full error message
      (k, v) => [snakeCaseToCamelCase(k), v],
    );

    const amount =
      values.amount === 'OTHER'
        ? values.otherAmount
        : // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
          values.amount && values.amount.toString();

    makeDonation({
      ...values,
      amount,
      campaignId: campaignId ? campaignId.toString() : null,
      ...extraParams,
    })
      .then((result) => {
        if (result.success) {
          trackEvent('Success: Created Donation', {
            amount: parseInt(amount, 10),
            campaign_id: campaignId,
          });
          invariant(result.invoiceIds, 'Missing invoiceIds');
          invariant(result.invoiceIds.length > 0, 'Missing invoiceIds');
          setInvoiceId(result.invoiceIds[0]);
          setSubmitted(true);
          setSubmittedAmount(amount);
        } else {
          invariant(result.error, 'missing error');
          // Convert Stripe error to general form error shape
          const error = {
            responseJson: {
              errors: [
                {
                  name: 'general',
                  type: result.error.stripeCode,
                  description: result.errorHuman,
                },
              ],
            },
          };
          trackEvent('Error: Processing Payment', {
            amount: values.amount,
            campaign_id: campaignId,
            error_type: result.error.stripeCode,
          });
          handleError(error);
        }

        scrollToTop();
        updateSubmitting(false);
      })
      .catch((e) => {
        const { errors: errorArray } = e.responseJson;
        scrollToTop();

        if (errorArray) {
          const errors = errorArray.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc: any, { name, code, description, data }: any) => {
              if (name === 'general') return acc;
              // eslint-disable-next-line no-param-reassign
              acc[name] = {
                name,
                code,
                description,
                data: data || {},
              };
              return acc;
            },
            {},
          );
          let requiredErrorMatched = false;
          Object.keys(errors).map((field) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (errors[field] && errors[field].code) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: errors[field].code,
                campaign_id: campaignId,
              });
            } else if (
              errors[field].description === 'Required' &&
              !requiredErrorMatched
            ) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: 'required_fields_missing',
                campaign_id: campaignId,
              });
              requiredErrorMatched = true;
            }

            return true;
          });
        }

        handleError(e);
        updateSubmitting(false);
      });
  };

  const initialValues = user
    ? {
        amount: '',
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        country: user.location.countryCode,
      }
    : {
        country: environment?.guessedLocation.countryCode || 'US',
      };
  return (
    <div data-qa-id="donate">
      <Container
        $imageHandle={bgImage?.handle}
        $mimetype={bgImage?.mimetype}
        minHeight={['100%', null, null, 'calc(100vh - 123px)']}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <div>
            <Box
              width={['100%', null, null, `calc(50% + ${gutterWidth * 2}px)`]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              {submitted ? (
                <Box
                  data-qa-id="donate-thank-you"
                  display={['block', null, 'flex']}
                  width="100%"
                >
                  <Box
                    display="flex"
                    flex="2"
                    flexDirection="column"
                    pr={gutterWidth / 2}
                  >
                    <Box
                      bg={colors.inputGrey}
                      color={colors.brandBlue}
                      borderRadius={15}
                      fontWeight={fontWeights.semiBold}
                      fontSize={[
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line no-nested-ternary
                        submittedAmount > 9999
                          ? 48
                          : submittedAmount > 999
                          ? 64
                          : 80,
                      ]}
                      py={40}
                      px={20}
                      classes={['flex', 'justify-center', 'align-center']}
                      data-qa-id="donate-thank-you-submitted-amount"
                    >
                      {`$${submittedAmount} USD`}
                    </Box>
                    <StyledSocialButtons
                      content={content}
                      message={content.metaDescription}
                      title={content.title}
                      subsite={content.subsite.publicName}
                    />
                    <Link
                      data-qa-id="donate-thank-you-receipt-link"
                      href={`/donation-receipts/${invoiceId}`}
                      target="donate_thank_you_receipt"
                    >
                      <Body textAlign="center">{getText('View Receipt')}</Body>
                    </Link>
                  </Box>
                  <Box
                    flex="3"
                    pl={[0, null, gutterWidth / 2]}
                    mt={[gutterWidth, null, 0]}
                  >
                    {thankYouHeadline && (
                      <Headline
                        text={thankYouHeadline}
                        // @ts-expect-error TS(2769): No overload matches this call.
                        isPageHeader={isPageHeader}
                      />
                    )}
                    {thankYouBody && (
                      <Body
                        dangerouslySetInnerHTML={{
                          __html: thankYouBody,
                        }}
                        mt={12}
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                <>
                  {headline && (
                    <Headline
                      text={headline}
                      // @ts-expect-error TS(2769): No overload matches this call.
                      isPageHeader={isPageHeader}
                    />
                  )}
                  {body && (
                    <Body
                      dangerouslySetInnerHTML={{
                        __html: body,
                      }}
                      mt={12}
                    />
                  )}

                  <Box my={[gutterWidth / 2, null, null, gutterWidth]}>
                    <Divider light marginTop={0} marginBottom={0} />
                  </Box>

                  <CmsDonateBlockInternalForm
                    onSubmit={onSubmit}
                    campaignId={campaignId}
                    initialValues={initialValues}
                  />
                </>
              )}
            </Box>
          </div>
        </Box>
      </Container>
    </div>
  );
}
